"use client";

import { FilePlus2, Plus, Save, Trash2, WandSparkles, X } from "lucide-react";
import { useState } from "react";
import type { OrderCategory, OrderLane, ParsedOrder, SoapSection, TemplatePreview } from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type TemplateBuilderModalProps = {
  initialSoap: Record<SoapSection, string>;
  initialOrders: ParsedOrder[];
  onClose: () => void;
  onSave: (template: TemplatePreview) => void;
};

type DraftOrder = {
  id: string;
  category: OrderCategory;
  label: string;
  details: string;
  lane: OrderLane;
  stat: boolean;
};

const SOAP_FIELDS: [SoapSection, string][] = [
  ["subjective", "Subjective"],
  ["objective", "Objective"],
  ["assessment", "Assessment"],
  ["plan", "Plan"],
];

const CATEGORIES: OrderCategory[] = ["medication", "lab", "procedure", "fluid", "other"];

let counter = 0;
const draftId = () => `tpl-draft-${Date.now()}-${counter++}`;

export function TemplateBuilderModal({ initialSoap, initialOrders, onClose, onSave }: TemplateBuilderModalProps) {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState(t("tb.subtitle.default"));
  const [soap, setSoap] = useState<Record<SoapSection, string>>({ ...initialSoap });
  const [orders, setOrders] = useState<DraftOrder[]>(
    initialOrders.map((order) => ({
      id: draftId(),
      category: order.category,
      label: order.label,
      details: order.details,
      lane: order.lane,
      stat: order.stat,
    })),
  );

  function updateOrder(id: string, patch: Partial<DraftOrder>) {
    setOrders((current) => current.map((order) => (order.id === id ? { ...order, ...patch } : order)));
  }

  function addOrder() {
    setOrders((current) => [
      ...current,
      { id: draftId(), category: "medication", label: "", details: "", lane: "one-day", stat: false },
    ]);
  }

  function save() {
    if (!name.trim()) return;
    const soapEntries = SOAP_FIELDS
      .map(([field]) => [field, soap[field].trim()] as const)
      .filter(([, value]) => value.length > 0);
    const template: TemplatePreview = {
      id: `custom-${Date.now()}`,
      name: name.trim(),
      subtitle: subtitle.trim() || "Custom template",
      soap: Object.fromEntries(soapEntries) as Partial<Record<SoapSection, string>>,
      orders: orders
        .filter((order) => order.label.trim())
        .map((order, index) => ({
          id: `${name.toLowerCase().replace(/\s+/g, "-")}-${index}`,
          category: order.category,
          label: order.label.trim(),
          details: order.details.trim() || "Needs confirmation",
          lane: order.lane,
          stat: order.stat,
          source: "template" as const,
        })),
    };
    onSave(template);
  }

  const orderCount = orders.filter((order) => order.label.trim()).length;
  const soapCount = SOAP_FIELDS.filter(([field]) => soap[field].trim()).length;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="template-builder template-builder-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tb-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="template-builder-head">
          <div><FilePlus2 size={18} /><h2 id="tb-title">{t("tb.title")}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <p className="template-builder-hint">
          {t("tb.hint")}
        </p>

        <div className="tb-scroll">
          <label className="template-builder-field">
            <span>{t("tb.name")}</span>
            <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={t("tb.name.ph")} />
          </label>
          <label className="template-builder-field">
            <span>{t("tb.desc")}</span>
            <input value={subtitle} onChange={(event) => setSubtitle(event.target.value)} placeholder={t("tb.desc.ph")} />
          </label>

          <div className="tb-section">
            <h3><WandSparkles size={14} /> {t("tpl.soapSkeleton")}</h3>
            <div className="tb-soap-grid">
              {SOAP_FIELDS.map(([field]) => (
                <label className="tb-soap-field" key={field}>
                  <span>{t(`soap.${field}`)}</span>
                  <textarea
                    value={soap[field]}
                    onChange={(event) => setSoap((current) => ({ ...current, [field]: event.target.value }))}
                    rows={3}
                    placeholder={`${t(`soap.${field}`)} ${t("tb.templateTextSuffix")}`}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="tb-section">
            <div className="tb-section-head">
              <h3><FilePlus2 size={14} /> {t("tb.orderSet")} ({orderCount})</h3>
              <button type="button" className="tb-add-order" onClick={addOrder}><Plus size={13} /> {t("tb.addOrder")}</button>
            </div>
            <div className="tb-order-list">
              {orders.length === 0 && <p className="tb-empty">{t("tb.empty")}</p>}
              {orders.map((order) => (
                <div className="tb-order-row" key={order.id}>
                  <input
                    className="tb-order-label"
                    value={order.label}
                    onChange={(event) => updateOrder(order.id, { label: event.target.value })}
                    placeholder={t("tb.order.name.ph")}
                    aria-label="Order name"
                  />
                  <input
                    className="tb-order-details"
                    value={order.details}
                    onChange={(event) => updateOrder(order.id, { details: event.target.value })}
                    placeholder={t("tb.order.usage.ph")}
                    aria-label="Order usage"
                  />
                  <select value={order.category} onChange={(event) => updateOrder(order.id, { category: event.target.value as OrderCategory })} aria-label="Category">
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>{t(`order.cat.${category}`)}</option>
                    ))}
                  </select>
                  <select value={order.lane} onChange={(event) => updateOrder(order.id, { lane: event.target.value as OrderLane })} aria-label="Lane">
                    <option value="one-day">{t("order.oneDay")}</option>
                    <option value="continuous">{t("order.continuous")}</option>
                  </select>
                  <button
                    type="button"
                    className={`tb-stat ${order.stat ? "on" : ""}`}
                    onClick={() => updateOrder(order.id, { stat: !order.stat })}
                    aria-pressed={order.stat}
                    title="STAT flag"
                  >
                    {t("order.stat")}
                  </button>
                  <button type="button" className="tb-remove" onClick={() => setOrders((current) => current.filter((item) => item.id !== order.id))} aria-label="Remove order">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="template-builder-actions">
          <span className="tb-count">{soapCount} {t("toast.soapFields")} · {orderCount} {t("toast.orders")}</span>
          <button type="button" className="secondary-button" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="primary-button" onClick={save} disabled={!name.trim()}>
            <Save size={15} /> {t("tb.save")}
          </button>
        </div>
      </section>
    </div>
  );
}
